import { Box, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import React, { useEffect, useMemo, useState } from 'react';
import { getJobResults } from '../../api/api';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import DownloadResults from './DownloadResults';
import { ResultsPaginationFooter } from './ResultsPaginationFooter';
import SearchCriteria from './SearchCritera';
import { getJob } from '../../storage/MySearchesStore';

// Material UI

// Import our store

// API

// Types
interface Props {
  jobID : string
}

type SearchResult = {
  job : string,
  tables : Record<string, SearchResultTable>,
}

type SearchResultTable = {
  name : string,
  columns : Array<GridColDef>,
  rows : Array<{}>,
}

interface RowType {
  id: number;
  [key: string]: string | number;
}

const INITIAL_WIDTH_LOOKUP : Record<string, number> = {
  "oligo_count": 150,
  "**oligo_position": 165,
  "oligo_id": 225,
  "target_region_sequence_20mer": 275,
  "gene_region": 500,
  "gc_content": 150,
  "oligo_id_sense": 250,
  "oligo_id_antisense": 250,
  "linear_score": 115,
  "ssrf_score": 215,
  "region": 125,
  "hits2microrna": 175,
  "refseq_seed": 200,
  "hits2accessions": 185,
  "hits2gids": 150,
  "gids": 115,
  "accessions": 250,
}

const COLUMN_SORT_TYPE_LOOKUP: Record<string, 'string' | 'number'> = {
  "oligo_count": 'number',
  "**oligo_position": 'number',
  "gc_content": 'number',
  "linear_score": 'number',
  "ssrf_score": 'number',
  "hits2microrna": 'number',
  "hits2accessions": 'number',
  "hits2gids": 'number',
  "hits2mouse": 'number',
  "hits2mm": 'number',
  "hits2mf": 'number',
  "hits2human": 'number',
  "hits2rat": 'number'
};

const COLUMN_NAME_LOOKUP : Record<string, string> = {
  "oligo_count": "Oligo Count",
  "oligo_id": "Oligo ID",
  "target_region_sequence_20mer": "Target Region Sequence 20mer",
  "gene_region": "Gene Region",
  "gc_content": "GC Content",
  "revcomp_guide": "Revcomp Guide",
  "linear_score": "Linear Score",
  "ssrf_score": "SSRF Score (ML ZScore)",
  "region": "Region",
  "hits2microrna": "Hits 2 MicroRNA",
  "refseq_seed": "RefSeq Seed",
  "hits2accessions": "Hits 2 Accessions",
  "hits2gids": "Hits 2 gIDs",
  "gids": "gIDs",
  "accessions": "Accessions",
  "hits2mouse": "Hits 2 Mouse",
  "hits2mm": "Hits 2 MM",
  "hits2mf": "Hits 2 MF",
  "hits2human": "Hits 2 Human",
  "hits2rat": "Hits 2 Rat",
}


const LIMIT_SIG_FIGS: Record<string, number> = {
  "ssrf_score": 3,
};

const VisualizeCSV = ({jobID}: Props) => {
  
  const [loading, setLoading] = useState<boolean>(true);
  const [resultsData, setResults] = useState<SearchResult | null>(null);
  const [csvData, setCsvData] = useState<string>("");
  const [searchString, setSearchString] = useState<string>("");

  const searchCriteria = useMemo(() => {
    return getJob(jobID)?.searchCriteria || null
  }, [jobID]);

  const getColumnWidth = (column : string, defaultSize : number = 250) : number => {
    return INITIAL_WIDTH_LOOKUP[column] || defaultSize;
  }  

  useEffect(() => {
    // If the user has specified a search string
    if (searchString) {
      // Clear the old results
      setResults(null);
      // Record that we are loading
      setLoading(true);
      // Use our API to get the job results from this jobID
      getJobResults(searchString).then((data) => {
        // Store the csv data returned from the server.
        let csvData = data as string;
        setCsvData(csvData);
        // Parse the results
        setResults(parseResults(csvData));
      }).catch((err) => {
        // If there is an error parsing the data, print the error.
        console.error(err);
      })
    }
  }, [searchString]);

  useEffect(() => {
    setSearchString(jobID);
  }, [jobID]);

  const parseResults = (data : string) : SearchResult => {
    // Define a list of allowed tables
    const ALLOWED_TABLES = ["ALL SEQUENCES", "SEQUENCES THAT TARGET MOUSE AND MM (Macaca Mulatta)", "TOP SCORING SEQUENCES"];

    // Define a placeholder for our results
    let results : SearchResult = {
      job : "",
      tables : {}
    };

    // Define some helper functions
    // When we are ready we can append the content that we have accumulated to our output object
    const append_data = () => {
      // Push the new table into our results object
      results.tables[current_category] = {
        name : current_category,
        columns : columns,
        rows : rows
      };
      // Clear the rows and columns
      columns = [];
      rows = [];
    };

    // Function to format the header
    const formatHeader = (header: string) => {
      // First see if we have an entry for this header.
      const overrideName = COLUMN_NAME_LOOKUP[header.toLocaleLowerCase()];
      if (overrideName) {
        return overrideName;
      } else {
        return header
      }

      // Remove all non-alphanumeric characters except underscores and digits
      let cleaned = header.replace(/[^a-zA-Z0-9_]/g, '');
    
      // Insert spaces around number groups
      cleaned = cleaned.replace(/(\d+)/g, ' $1 ');
    
      // Replace underscores with spaces
      cleaned = cleaned.replace(/_/g, ' ');
    
      // Normalize spaces
      cleaned = cleaned.replace(/\s+/g, ' ').trim();
    
      // Capitalize each word
      return cleaned
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    };
    
    // Here we accumulate the data we are parsing so we can build appropriate tables. 
    let current_category = "";
    let columns : Array<GridColDef> = [];
    let rows : Array<{}> = [];

    // Simple hash function to convert string to number
    function fnv1aHash(str: string): number {
      let hash = 2166136261;
      for (let i = 0; i < str.length; i++) {
        hash ^= str.charCodeAt(i);
        hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
      }
      return hash >>> 0; // Convert to unsigned 32-bit integer
    }
    
    function hashStringToHue(str: string): number {
      // Extract first number from the string
      const match = str.match(/\d+/);
      if (match) {
        const num = parseInt(match[0], 10);
        const largePrime = 15485863;
        return (num * largePrime) % 360;
      } else {
        // fallback to fnv1aHash if no number found
        const hash = fnv1aHash(str);
        return hash % 360;
      }
    }      

    // Try to parse the results
    try{
      // Split into lines on new line character.
      const lines = data.split('\n');

      // Flags
      let is_header_line : boolean = false;

      // Parse
      for (let line_raw of lines) {
        // Trim the whitespace. 
        let line = line_raw.trim();
        
        // Check if the line is empty
        if (line.length <= 0) {
          continue; // No info on this line. 
        } else {
          if (line.replaceAll(',', '').trim().length <= 0) {
            continue; // No info on this line. 
          }
        }

        // Check if this line is defining a category
        if (line.startsWith("###")){
          // Parse the category out of this line.
          while (line.includes("###")) {
            // Remove the occurrence
            line = line.replace("###", ''); 
          }
          // After we have removed all of the ###, trim the line
          line = line.trim();
          // We are defining a new category. 
          console.log("Update Category:", line);
          // When we update the category, we need to flush the data we have accumulated to the last table unless this is the first table. 
          if (current_category) {
            append_data();
          }
          // Update that we are about to parse a header
          is_header_line = true;
          // Update the current category
          current_category = line;
          // Continue onwards.
          continue;
        }

        // If we get here we are in a category and have a valid line.
        if(ALLOWED_TABLES.includes(current_category)){
          if (is_header_line) {
            // Clear the existing header
            // columns = [{field:'id', headerName:'id'}];
            // No meta data has been defined yet, so lets figure out the headers for all of the columns of data
            let headers = line.split(',');
            // Assign to the metadata field
            for (let header of headers) {
              const field : string =  header.trim().toLocaleLowerCase().replaceAll(" ", "_");
              let column : GridColDef = {
                field,
                headerName: formatHeader(header),
                width: getColumnWidth(field),
                headerAlign: 'center',
                align: field.includes('oligo') ? 'left' : 'center',
                type: COLUMN_SORT_TYPE_LOOKUP[field] || 'string',
              };

              if (LIMIT_SIG_FIGS[field] != undefined && COLUMN_SORT_TYPE_LOOKUP[field] === 'number') {
                const sigFigs = LIMIT_SIG_FIGS[field];
                column.valueFormatter = (rawValue: any) => {
                  const numericValue = parseFloat(rawValue);
                  if (isNaN(numericValue)) return numericValue;
                  return numericValue.toFixed(sigFigs);
                };
              }

              if (field === "accessions" || field === "gids") {
                column["renderCell"] = (params) => {
                  const accessions: string[] = (params.value || '').split(';').filter(Boolean);
                  return (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      flexWrap="wrap"
                      useFlexGap
                      alignItems="center" // vertically center in the DataGrid cell
                      sx={{
                        width: '100%',
                        height: '100%',
                        alignContent: 'center', // helps when wrapped
                      }}
                    >
                      {accessions.map((acc, index) => {
                        const hue = hashStringToHue(acc);
                        const backgroundColor = `hsl(${hue}, 70%, 85%)`;  // pastel background
                        const textColor = `hsl(${hue}, 80%, 25%)`;        // dark version for text

                        return (
                          <Chip
                            key={index}
                            label={acc}
                            size="small"
                            sx={{
                              backgroundColor,
                              color: textColor,
                              border: 'none',
                              fontWeight: 500,
                            }}
                          />
                        );
                      })}
                    </Stack>
                  );
                };                
              }

              columns.push(column);
            }
            // We are done parsing the header line
            is_header_line = false;
            // Continue
            continue;
          } else {
            // Line data
            let line_data = line.split(',');
            // Now we need to attribute this value, to the header it should be associated with
            let row : RowType = {id: rows.length}
            for (let indexString in line_data) {
              let index = parseInt(indexString);
              if (index < columns.length) {
                
                row[columns[index].field] = line_data[index];
              }
            }
            rows.push(row)
          }
        }
      }
    } catch (error) {
      // If there is an error, print the error. 
      console.log(error);
    } finally {
      // We need to append the last category
      if (current_category) {
        append_data();
      }
      // Finally, indicate that we are no longer loading.
      setLoading(false);
    }

    // Return the results
    return results;
  }

  const renderTable = (table: SearchResultTable | undefined) => {
    if (!table) return <></>;
  
    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          overflow: 'hidden', // prevent spillover
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DataGrid
          rows={table.rows}
          columns={table.columns}
          pageSizeOptions={[]}
          getRowHeight={() => 'auto'}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
          }
          slots={{ pagination: ResultsPaginationFooter }}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            fontFamily: 'monospace',

            '& .MuiDataGrid-container--top [role="row"]': {
              background: '#1876d2',
              color: '#fff',
            },

            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
            },

            '& .MuiDataGrid-cellContent': {
              display: 'flex',
              alignItems: 'center',
              height: '100%',
              width: '100%',
            },

            '& .even': {
              backgroundColor: '#F0F0F0',
            },

            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#1976d224',
            },

            // ✅ Highlight full row when focused (clicked)
            '& .MuiDataGrid-row.Mui-hovered, & .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.MuiDataGrid-row--editing': {
              outline: '2px solid #1976d2',
              outlineOffset: '-2px',
              backgroundColor: '#1976d220',
            },

            // Optional: remove the default cell focus outline
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },

            transform: 'translateY(-2px)',
          }}
          autoHeight={false}
        />
      </Box>
    );
  };
  

  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const a11yProps = (index: number) => {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
  }
  
  function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
  
    return (
      <Box
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        sx={{
          flex: 1,
          minHeight: 0,
          display: value === index ? 'flex' : 'none',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        {...other}
      >
        {children}
      </Box>
    );
  }

  if (loading) {
    return <CircularProgress size={100} sx={{margin:'auto'}}/>
  }

  return (
    <Stack direction="column" sx={{height: '100%' }}>
      {/* New Row Above Paper (e.g., could be a header, filters, or anything else) */}
      <Paper elevation={10} sx={{m:2, mb:0, p:2, pb:0, display: 'flex', flexDirection: 'column', borderRadius:'12px', overflow:'hidden' }}>
        <Stack direction={'row'} gap={4} height={'190px'}>
          {/* How to Interpret Results */}
          <div>
            <Typography variant="h6" fontWeight="bold" color={"#000000"}>
              Results Summary
            </Typography>
            <Typography component="div" sx={{height:'calc(100% - 44px)', overflowY:'auto'}}>
              <ol style={{ paddingLeft: '1.5rem', margin:'0px' }}>
                <li><strong>Predicted Efficacy (Score):</strong> The anticipated effectiveness of the siRNA.</li>
                <li><strong>Target Engagement:</strong> The isoforms of the specific target ID that are being targeted.</li>
                <li><strong>Specificity:</strong> The likelihood of off-target effects; ensures unique targeting of genes.</li>
                <li><strong>No Overlap with Known miRNAs:</strong> Avoids regions with miRNA-like effects to reduce risk.</li>
                <li><strong>CMC (Chemistry Manufacturing Controls):</strong> Predicts synthesis/stability risks in sequences.</li>
                <li><strong>Cross-Reactivity with Other Species:</strong> Ensures conservation for cross-species validation.</li>
              </ol>
            </Typography>
          </div>
          {/* Spacer Box */}
          <Box flex={1}/>
          {/* Search Criteria if available */}
          {searchCriteria && <SearchCriteria species={searchCriteria.species} geneName={searchCriteria.gene} accessionNumber={searchCriteria.accessionNumber || "Custom Gene Sequence"} customSequence={searchCriteria.sequence} />}
        </Stack>
      </Paper>

      {/* Existing Paper Component */}
      <Paper elevation={10} sx={{m:2, mb:0, height: '100%', minHeight: 0, flex: 1, display: 'flex', flexDirection: 'column', borderRadius:'12px' }}>
        <Stack direction={"row"} sx={{borderRadius:'12px'}}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="results tabs"
            variant="scrollable"
            scrollButtons={false}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              backgroundColor: '#f9f9f9',
              borderRadius:'12px 12px 0 0',
              pl: 1, 
              '& .MuiTabs-flexContainer': {
                gap: 1,
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 700, // Always reserve space for bold
                fontSize: '1rem',
                border: '1px solid #ccc',
                borderRadius: '12px 12px 0 0',
                backgroundColor: '#fff',
                transition: 'all 0.2s ease-in-out',
                transform: 'translateY(4px)',
                color: 'rgba(0, 0, 0, 0.6)', // unselected tab color
              },
              '& .Mui-selected': {
                color: 'rgb(25, 129, 215)',
                backgroundColor: '#e3f2fd',
                borderColor: '#2196f3 #2196f3 transparent #2196f3',
              }
            }}                
          >
            <Tab label="Top Scoring Sequences" {...a11yProps(0)} />
            <Tab label="Sequences Targeting Mouse and Macaca Mulatta" {...a11yProps(1)} />
            <Tab label="All Sequences" {...a11yProps(2)} />
          </Tabs>
          {/* Flexbox to take up available horizontal space */}
          <Box flex={1}/>
          {/* Download CSV Button */}
          <Box sx={{ display: 'flex', alignItems: 'center', pr: 2 }}>
            <DownloadResults csv_data={csvData} file_name={jobID} />
          </Box>
        </Stack>

        <CustomTabPanel value={value} index={0}>
          {resultsData && renderTable(resultsData.tables["TOP SCORING SEQUENCES"])}
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          {resultsData && renderTable(resultsData.tables["SEQUENCES THAT TARGET MOUSE AND MM (Macaca Mulatta)"])}
        </CustomTabPanel>
        <CustomTabPanel value={value} index={2}>
          {resultsData && renderTable(resultsData.tables["ALL SEQUENCES"])}
        </CustomTabPanel>
      </Paper>
    </Stack>
  );
  
};

export default VisualizeCSV;